import type { CognitoUser } from '@livecontrol/scheduler/model';
import { assert } from '@sindresorhus/is';
import _ from 'lodash';
import shallow from 'zustand/shallow';
import type { Token } from '../../model';
import { store } from '../factory';
import * as Storage from '../storage';
import type { Store } from '../store';
import * as TokenStore from '../token-store';
import * as Dbx_ from './dbx';

/* eslint-disable @typescript-eslint/no-unused-vars */
export import Dbx = Dbx_;

/**
 * React hook that returns the authenticated user (AKA `me`) or the client for whom
 * an administrator is masquerading.
 *
 * @returns Authenticated user. Throws an exception if no user has been authenticated.
 * @throws An exception if no user has been authenticated.)
 */
export const useMe = (): CognitoUser => {
  // Get both the actual user (`me`) and, potentially, the client for whom an adminstrator is masquerading (`thee`).
  const { me } = store((state: Store.State) => _.pick(state, ['me']), shallow);

  // If this is undefined, you've made a mistake.
  assert.object(me);

  // If an administrator is masquerading as a client, return the client.
  return me;
};

/**
 * React hook that returns the current state of the indentity parameters.
 *
 * @returns Identity parameters (token, user, client)
 */
export const useIdentity = (): {
  me?: CognitoUser;
  token?: Token;
} => store((state: Store.State) => _.pick(state, ['me', 'token']), shallow);

/**
 * Logs in the user associated with the specified authentication token.
 *
 * @param tokens - Authentication tokens.
 * @param tokens.token - Authentication token for command center
 * @param tokens.webplayerToken - Authentication token for webplayer
 * @param options - Storage options (persistent/local vs. transient/session)
 */
export const login = (tokens: { token: Token; webplayerToken: string }, options?: Storage.Options): void => {
  TokenStore.setTokens(tokens, options);
};

/**
 * Logs out the current user and resets the authentication store.
 */
export const logout = (): void => {
  Storage.removeItem(Storage.Tokens.COMMAND_CENTER_KEY);
  Storage.removeItem(Storage.Tokens.WEBPLAYER_KEY);

  TokenStore.setTokens(undefined);
};

export { useAuthenticate } from './use-authenticate';
export { useConfirmUser } from './use-confirm-user';
export { useForgotPassword } from './use-forgot-password';
export { useConfirmForgotPassword } from './use-confirm-forgot-password';
export { useUser } from './use-user';
